import React, {useEffect, useMemo, useState} from 'react';
import {Button, Modal, message} from 'antd';
import TableComponent from '../../../components/molecules/Table/component';
import {bestPromotersTableColumns} from '../../../components/molecules/Table/columns';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../store/rootReducer';
import {getCookie} from 'typescript-cookie';
import promoterService from '../../../services/promoterService';
import {handleLogout, createUserTableChangeHandler, useIsMobile} from '../../../utils/helpers';
import {useNavigate} from 'react-router-dom';
import {ExclamationCircleOutlined} from '@ant-design/icons';
import {mobileRenderers} from "../../../components/molecules/Table/tableMobileRenderers";

const {confirm, success} = Modal;

const BestPromotersPage: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const currentUser: any = useSelector((state: RootState) => state.auth.user);
    const role = getCookie('roleType') || currentUser?.roles?.[0]?.roleType;
    const isMobile = useIsMobile();

    const [promoterList, setPromoterList] = useState<any[]>([]);
    const showBecomePromoter = role === 'advertiser' && currentUser.roles.length === 1;

    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        userType: '',
        search: '',
        sortBy: '',
        sortDirection: 'desc' as 'asc' | 'desc',
    });

    const onTableChange = useMemo(() =>
        createUserTableChangeHandler(filters, setFilters), [filters]
    );

    const onLogout = () => {
        handleLogout(dispatch, navigate)();
    };

    useEffect(() => {
        const fetchPromoters = async () => {
            setLoading(true);
            try {
                const data = await promoterService.getBestPromoters(filters);
                setPromoterList(data);
            } catch (error) {
                console.error('Error fetching promoters:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchPromoters();
    }, [filters]);

    const handleRowClick = (record: any) => {
        navigate(`/promoters/${record._id}`);
    };

    const handleBecomePromoter = () => {
        confirm({
            title: 'Add Promoter Role',
            icon: <ExclamationCircleOutlined/>,
            content: (
                <>
                    By confirming, you’ll add the <b>Promoter</b> role to your account.
                    You can explore the new Promoter Portal right away, and—when you’re ready
                    to actually create affiliate links and get paid for each click you bring—you’ll find instructions
                    there on activating your Promoter subscription.
                </>
            ),
            okText: 'Confirm',
            cancelText: 'Cancel',
            async onOk() {
                try {
                    const res = await promoterService.becomePromoter();
                    if (res.status === 200) {
                        success({
                            title: 'Success!',
                            content: (
                                <>
                                    Your account now has both {role} and promoter roles.
                                    To apply this change, please log out and then log back in.
                                </>
                            ),
                            okText: 'Log out',
                            onOk: onLogout
                        });
                    } else {
                        message.error('Error while adding promoter role.');
                    }
                } catch (err: any) {
                    message.error(err.message || 'Error while adding promoter role.');
                }
            }
        });
    };

    return (
        <div style={{position: 'relative', paddingTop: isMobile ? (showBecomePromoter ? '50px' : '40px') : '20px'}}>
            {showBecomePromoter && (
                <div style={{position: 'absolute', top: 0, right: 0, margin: '10px'}}>
                    <Button
                        type="primary"
                        size={isMobile ? 'small' : 'middle'}
                        style={isMobile ? {fontSize: 12, padding: '0 10px', height: 28} : undefined}
                        onClick={handleBecomePromoter}
                    >
                        Become a promoter
                    </Button>
                </div>
            )}
            <TableComponent columns={bestPromotersTableColumns} title={"Best promoters"} data={promoterList || []}
                            onRowClick={handleRowClick} loading={loading} onChange={onTableChange} isMobile={isMobile}
                            mobileVariant="bestPromoters" mobileRenderers={mobileRenderers}
                            rowKey={(row) => row._id}
            />
        </div>
    );
};

export default BestPromotersPage;
